/**
 * transitions.js - CSS transitions fallback for View Transitions API
 *
 * Provides smooth transitions for browsers that don't support View Transitions API
 * by adding/removing CSS classes during swap operations.
 *
 * Classes applied:
 * - .htmlstar-swapping - During the swap operation
 * - .htmlstar-settling - After swap, during settle animation
 *
 * CSS Example:
 * .htmlstar-swapping { opacity: 0; transition: opacity 150ms; }
 * .htmlstar-settling { opacity: 1; transition: opacity 150ms; }
 */

/**
 * Default transition duration in milliseconds
 */
const DEFAULT_DURATION = 150;

/**
 * Check if View Transitions API is supported
 * @returns {boolean}
 */
export function hasViewTransitions() {
  return typeof document !== 'undefined' &&
    typeof document.startViewTransition === 'function';
}

/**
 * Wait for CSS transitions to complete on an element
 * @param {HTMLElement} el - Element to wait on
 * @param {number} timeout - Maximum wait time in ms
 * @returns {Promise<void>}
 */
function waitForTransition(el, timeout = DEFAULT_DURATION) {
  return new Promise(resolve => {
    let resolved = false;

    const done = () => {
      if (!resolved) {
        resolved = true;
        el.removeEventListener('transitionend', done);
        resolve();
      }
    };

    el.addEventListener('transitionend', done, { once: true });

    // Fallback timeout in case no transition is defined
    setTimeout(done, timeout + 50);
  });
}

/**
 * Perform a swap with CSS transition fallback
 *
 * @param {HTMLElement} target - Element to swap
 * @param {Function} doSwap - Function that performs the actual swap
 * @param {Object} options - Transition options
 * @param {number} options.duration - Transition duration in ms
 * @param {boolean} options.skipFallback - Skip CSS fallback even without View Transitions
 * @returns {Promise<void>}
 */
export async function swapWithTransition(target, doSwap, options = {}) {
  const {
    duration = DEFAULT_DURATION,
    skipFallback = false
  } = options;

  // Use View Transitions API if available
  if (hasViewTransitions()) {
    return document.startViewTransition(doSwap).finished;
  }

  // Skip fallback if requested
  if (skipFallback) {
    doSwap();
    return Promise.resolve();
  }

  // CSS transitions fallback
  try {
    // Start swapping state
    target.classList.add('htmlstar-swapping');
    await waitForTransition(target, duration);

    // Perform swap
    doSwap();

    // Remove swapping, add settling
    target.classList.remove('htmlstar-swapping');
    target.classList.add('htmlstar-settling');

    // Wait for settle transition
    await waitForTransition(target, duration);

    // Clean up
    target.classList.remove('htmlstar-settling');
  } catch (err) {
    // Ensure cleanup on error
    target.classList.remove('htmlstar-swapping', 'htmlstar-settling');
    throw err;
  }
}

/**
 * Apply view-transition-name to an element for smoother transitions
 * Useful for elements that should animate independently
 *
 * @param {HTMLElement} el - Element to name
 * @param {string} name - Transition name
 */
export function setTransitionName(el, name) {
  el.style.viewTransitionName = name;
}

/**
 * Clear view-transition-name from an element
 * @param {HTMLElement} el - Element to clear
 */
export function clearTransitionName(el) {
  el.style.viewTransitionName = '';
}

/**
 * Inject default CSS for transitions fallback
 * Call once at initialization if you want default styles
 */
export function injectDefaultStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('htmlstar-transitions-css')) return;

  const style = document.createElement('style');
  style.id = 'htmlstar-transitions-css';
  style.textContent = `
    .htmlstar-swapping {
      opacity: 0;
      transition: opacity ${DEFAULT_DURATION}ms ease-out;
    }
    .htmlstar-settling {
      opacity: 1;
      transition: opacity ${DEFAULT_DURATION}ms ease-in;
    }
  `;
  document.head.appendChild(style);
}
